/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/Experts/AdminManageExperts.tsx.
 */

import React, { useEffect, useState, useMemo } from "react";
import axiosClient from "../../../../api/axiosClient";
import DataTable, { Column, FilterConfig } from "../../../../components/common/DataTable";
import "./AdminManageExperts.css";

interface Expert {
  id: string;
  userCode: string;
  name: string;
  email: string;
  role: string;
  expertProfile?: {
    id: number;
    materialId: number;
    material?: { id: number; name: string; code: string } | null;
  } | null;
  createdAt?: string;
  materialName?: string; // For filtering
}

interface Material {
  id: number;
  name: string;
}

interface CreateExpertForm {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  materialId: number | null;
}

const AdminManageExperts: React.FC = () => {
  const [experts, setExperts] = useState<Expert[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateExpertForm>({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    materialId: null,
  });

  // Search and filter state
  const [search, setSearch] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string>>({
    materialName: "",
  });

  // Fetch experts list (fetch all for client-side pagination)
  const fetchExperts = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get("/admin/experts");

      if (res.data?.success) {
        // Flatten material name for filtering - handle expertProfile.material structure
        const expertsData = (res.data.data || []).map((expert: any) => ({
          ...expert,
          materialName: expert.expertProfile?.material?.name || "", // Add materialName for filtering
        }));
        setExperts(expertsData);
      }
    } catch (err) {
      console.error("Failed to fetch experts:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch materials for dropdown
  const fetchMaterials = async () => {
    try {
      const res = await axiosClient.get("/materials");
      if (res.data?.success) {
        setMaterials(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch materials:", err);
    }
  };

  useEffect(() => {
    fetchExperts();
    fetchMaterials();
  }, []);

  // Get unique material names for filter
  const materialNames = useMemo(() => {
    const names = new Set<string>();
    experts.forEach((expert: Expert) => {
      const materialName = expert.expertProfile?.material?.name || expert.materialName || "";
      if (materialName) {
        names.add(materialName);
      }
    });
    return Array.from(names).sort();
  }, [experts]);

  // Form input handler
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "materialId" ? (value ? Number(value) : null) : value,
    }));
  };

  // Create Expert Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // validation
    if (!formData.name.trim() || !formData.email.trim()) {
      setError("Name and Email are required.");
      return;
    }

    if (!formData.password || !formData.confirmPassword) {
      setError("Password and Confirm Password are required.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        password: formData.password.trim(),
        role: "EXPERT", // expert=REVIEWER
        materialId: formData.materialId,
      };

      const res = await axiosClient.post("/admin/experts", payload);

      if (res.data?.success) {
        setShowModal(false);

        // reset
        setFormData({
          name: "",
          email: "",
          password: "",
          confirmPassword: "",
          materialId: null,
        });

        fetchExperts();
      } else {
        setError(res.data?.message || "Failed to create expert.");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to create expert.");
    } finally {
      setSubmitting(false);
    }
  };

  // Column definitions
  const columns: Column<Expert>[] = [
    {
      key: "userCode",
      label: "User Code",
      width: "12%",
    },
    {
      key: "name",
      label: "Name",
      render: (item) => (
        <div style={{ fontWeight: 600, color: "#2d2730" }}>{item.name}</div>
      ),
      width: "18%",
    },
    {
      key: "email",
      label: "Email",
      render: (item) => (
        <div>
          <div style={{ color: "#2d2730" }}>{item.email}</div>
        </div>
      ),
      width: "22%",
    },
    {
      key: "role",
      label: "Role",
      render: (item) => (
        <span className="role-badge">EXPERT</span>
      ),
      width: "12%",
    },
    {
      key: "materialName",
      label: "Assigned Material",
      render: (item) => (
        <span style={{ fontWeight: 500 }}>
          {item.expertProfile?.material?.name || item.materialName || "-"}
        </span>
      ),
      width: "18%",
    },
    {
      key: "createdAt",
      label: "Created At",
      render: (item) => (
        <div>
          <div>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"}</div>
          {item.createdAt && (
            <div style={{ fontSize: "0.85rem", color: "#6b6b6b", marginTop: "2px" }}>
              {new Date(item.createdAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          )}
        </div>
      ),
      width: "18%",
    },
  ];

  // Filter configurations - filter by materialName (flattened field)
  const filters: FilterConfig[] = useMemo(() => {
    if (materialNames.length === 0) return [];
    return [
      {
        key: "materialName",
        label: "Material",
        options: materialNames.map((name) => ({
          value: name,
          label: name,
        })),
        defaultValue: "",
      },
    ];
  }, [materialNames]);

  // Handle filter changes
  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  // Filter experts based on search
  const filteredExperts = useMemo(() => {
    let result = [...experts];

    // Apply search filter
    if (search.trim()) {
      const searchLower = search.toLowerCase().trim();
      result = result.filter((expert) => {
        const name = expert.name?.toLowerCase() || "";
        const email = expert.email?.toLowerCase() || "";
        const userCode = expert.userCode?.toLowerCase() || "";
        const materialName = (expert.expertProfile?.material?.name || expert.materialName || "").toLowerCase();
        return name.includes(searchLower) || 
               email.includes(searchLower) || 
               userCode.includes(searchLower) || 
               materialName.includes(searchLower);
      });
    }

    // Apply material filter
    if (filterValues.materialName) {
      result = result.filter((expert) => {
        const materialName = expert.expertProfile?.material?.name || expert.materialName || "";
        return materialName === filterValues.materialName;
      });
    }

    return result;
  }, [experts, search, filterValues]);

  const closeModal = () => {
    setShowModal(false);
    setError(null);

    setFormData({
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
      materialId: null,
    });
  };

  return (
    <div className="background-form-container analysis-shell">
      <div className="analysis-banner">
        <span className="analysis-banner__title">Manage Experts</span>
      </div>
      <div className="manage-experts-container">
      {/* Custom Header: Search and Filter on left, Button on right */}
      <div className="manage-experts-header">
        <div className="manage-experts-left">
          <div className="data-table-search">
            <svg
              className="search-icon"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 14L11.1 11.1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              type="text"
              placeholder="Search expert by name, email, code, or material..."
              className="data-table-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {filters.length > 0 && (
            <div className="data-table-filters">
              {filters.map((filter) => (
                <select
                  key={filter.key}
                  className="data-table-filter-select"
                  value={filterValues[filter.key] || ""}
                  onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                >
                  <option value="">All {filter.label}</option>
                  {filter.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ))}
            </div>
          )}
        </div>
        <div className="manage-experts-actions">
          <button className="add-expert-btn" onClick={() => setShowModal(true)}>
            + Add Expert
          </button>
        </div>
      </div>
      <DataTable<Expert & { materialName?: string }>
        data={filteredExperts}
        columns={columns}
        loading={loading}
        pagination={true}
        itemsPerPage={10}
        emptyMessage="No experts found"
      />

      {/* Add Expert Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Add New Expert</h3>
              <button className="modal-close-btn" onClick={closeModal}>×</button>
            </div>

            <form className="expert-form" onSubmit={handleSubmit}>
              {error && <div className="form-error">{error}</div>}

              <div className="form-group">
                <label>Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input type="password" name="password" value={formData.password} onChange={handleInputChange} required />
              </div>

              <div className="form-group">
                <label>Confirm Password *</label>
                <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} required />
              </div>

              <div className="form-group">
                <label>Assign Material</label>
                <select name="materialId" value={formData.materialId ?? ""} onChange={handleInputChange}>
                  <option value="">-- Select Material --</option>
                  {materials.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={closeModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={submitting}>
                  {submitting ? "Adding..." : "Add Expert"}
                </button>
              </div>
            </form>

          </div>
        </div>
      )}
      </div>
    </div>
  );
};

/** React component — Admin Manage Experts. */
export default AdminManageExperts;